import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, FileSignature, Receipt, CheckCircle, Clock } from 'lucide-react';
import { statusColors, statusLabels } from './constants';

// Dialog Detalhes Orçamento
export function DetalhesOrcamentoDialog({ open, onOpenChange, orcamento, onDownloadPDF }) {
  if (!orcamento) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Detalhes do Orçamento
          </DialogTitle>
          <DialogDescription>{orcamento.numero_orcamento}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-semibold text-slate-700 mb-2">Cliente</h4>
            <p className="text-lg font-medium">{orcamento.cliente_nome}</p>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500 uppercase">Status</p>
              <Badge className={`mt-1 ${statusColors[orcamento.status] || 'bg-gray-100'}`}>
                {statusLabels[orcamento.status] || orcamento.status}
              </Badge>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500 uppercase">Data Criação</p>
              <p className="font-semibold">{orcamento.data_criacao ? new Date(orcamento.data_criacao).toLocaleDateString('pt-BR') : 'N/A'}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500 uppercase">Validade</p>
              <p className="font-semibold">{orcamento.data_validade ? new Date(orcamento.data_validade).toLocaleDateString('pt-BR') : 'N/A'}</p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-700 mb-2">Itens do Orçamento</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2">Item</th>
                    <th className="text-center p-2 w-16">Qtd</th>
                    <th className="text-right p-2 w-24">Valor Unit.</th>
                    <th className="text-center p-2 w-16">Desc.</th>
                    <th className="text-right p-2 w-24">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {orcamento.itens?.map((item, index) => {
                    const subtotal = (item.quantidade || 1) * (item.valor_unitario || 0) * (1 - (item.desconto_percentual || 0) / 100);
                    return (
                      <tr key={index} className="border-t">
                        <td className="p-2">{item.descricao || item.material_nome || 'Item'}</td>
                        <td className="p-2 text-center">{item.quantidade || 1}</td>
                        <td className="p-2 text-right">R$ {(item.valor_unitario || 0).toFixed(2)}</td>
                        <td className="p-2 text-center">{item.desconto_percentual || 0}%</td>
                        <td className="p-2 text-right font-medium">R$ {subtotal.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr className="border-t-2">
                    <td colSpan={4} className="p-2 text-right font-semibold">TOTAL:</td>
                    <td className="p-2 text-right font-bold text-lg">
                      R$ {(orcamento.valor_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          
          {orcamento.condicoes_pagamento && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-1">Condições de Pagamento</h4>
              <p className="text-blue-700">{orcamento.condicoes_pagamento}</p>
            </div>
          )}
          
          {orcamento.observacoes && (
            <div className="bg-amber-50 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-1">Observações</h4>
              <p className="text-amber-700">{orcamento.observacoes}</p>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
          <Button onClick={() => onDownloadPDF(orcamento.id)}>
            <Download className="w-4 h-4 mr-2" />
            Baixar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog Detalhes Contrato
export function DetalhesContratoDialog({ open, onOpenChange, contrato, onDownloadPDF }) {
  if (!contrato) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-blue-600" />
            Detalhes do Contrato
          </DialogTitle>
          <DialogDescription>{contrato.numero_contrato}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-semibold text-slate-700 mb-2">Cliente</h4>
            <p className="text-lg font-medium">{contrato.cliente_nome}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500 uppercase">Status</p>
              <Badge className={`mt-1 ${statusColors[contrato.status] || 'bg-gray-100'}`}>
                {statusLabels[contrato.status] || contrato.status}
              </Badge>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500 uppercase">Assinatura</p>
              {contrato.assinatura ? (
                <Badge className="mt-1 bg-emerald-100 text-emerald-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Assinado
                </Badge>
              ) : (
                <Badge className="mt-1 bg-amber-100 text-amber-800">
                  <Clock className="w-3 h-3 mr-1" />
                  Pendente
                </Badge>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500 uppercase">Tipo</p>
              <p className="font-semibold capitalize">{contrato.tipo}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-xs text-green-600 uppercase">Valor Mensal</p>
              <p className="font-bold text-green-700">
                R$ {(contrato.valor_mensal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className={`rounded-lg p-3 text-center ${contrato.dias_para_vencer < 30 ? 'bg-red-50' : 'bg-slate-50'}`}>
              <p className={`text-xs uppercase ${contrato.dias_para_vencer < 30 ? 'text-red-600' : 'text-slate-500'}`}>Dias p/ Vencer</p>
              <p className={`font-semibold ${contrato.dias_para_vencer < 30 ? 'text-red-700' : ''}`}>
                {contrato.dias_para_vencer || 'N/A'} dias
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-xs text-blue-600 uppercase">Data Início</p>
              <p className="font-semibold text-blue-700">
                {contrato.data_inicio ? new Date(contrato.data_inicio).toLocaleDateString('pt-BR') : 'N/A'}
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-xs text-blue-600 uppercase">Data Fim</p>
              <p className="font-semibold text-blue-700">
                {contrato.data_fim ? new Date(contrato.data_fim).toLocaleDateString('pt-BR') : 'N/A'}
              </p>
            </div>
          </div>
          
          {contrato.servicos?.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-700 mb-2">Serviços Inclusos</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-2">Serviço</th>
                      <th className="text-center p-2 w-24">Qtd Inclusa</th>
                      <th className="text-right p-2 w-24">Valor Unit.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contrato.servicos.map((servico, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-2">{servico.descricao || 'Serviço'}</td>
                        <td className="p-2 text-center">{servico.quantidade_inclusa || 0}</td>
                        <td className="p-2 text-right">R$ {(servico.valor_unitario || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {contrato.assinatura && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Informações da Assinatura
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm text-emerald-700">
                <p><strong>Signatário:</strong> {contrato.assinatura.nome_signatario}</p>
                <p><strong>CPF:</strong> {contrato.assinatura.cpf_signatario || 'N/A'}</p>
                <p><strong>Email:</strong> {contrato.assinatura.email_signatario || 'N/A'}</p>
                <p><strong>Data:</strong> {contrato.assinatura.data_assinatura ? new Date(contrato.assinatura.data_assinatura).toLocaleString('pt-BR') : 'N/A'}</p>
              </div>
            </div>
          )}
          
          {contrato.observacoes && (
            <div className="bg-amber-50 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-1">Observações</h4>
              <p className="text-amber-700">{contrato.observacoes}</p>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
          <Button onClick={() => onDownloadPDF(contrato.id)}>
            <Download className="w-4 h-4 mr-2" />
            Baixar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog Detalhes Fatura
export function DetalhesFaturaDialog({ open, onOpenChange, fatura, onMarcarPago }) {
  if (!fatura) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-blue-600" />
            Detalhes da Fatura
          </DialogTitle>
          <DialogDescription>{fatura.numero_fatura}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="font-semibold text-slate-700 mb-2">Cliente</h4>
            <p className="text-lg font-medium">{fatura.cliente_nome}</p>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500 uppercase">Status</p>
              <Badge className={`mt-1 ${statusColors[fatura.status] || 'bg-gray-100'}`}>
                {statusLabels[fatura.status] || fatura.status}
              </Badge>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-xs text-green-600 uppercase">Valor Total</p>
              <p className="font-bold text-green-700 text-lg">
                R$ {(fatura.valor_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-500 uppercase">Emissão</p>
              <p className="font-semibold">
                {fatura.data_criacao ? new Date(fatura.data_criacao).toLocaleDateString('pt-BR') : 'N/A'}
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-xs text-blue-600 uppercase">Período Início</p>
              <p className="font-semibold text-blue-700">
                {fatura.periodo_inicio ? new Date(fatura.periodo_inicio).toLocaleDateString('pt-BR') : 'N/A'}
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-xs text-blue-600 uppercase">Período Fim</p>
              <p className="font-semibold text-blue-700">
                {fatura.periodo_fim ? new Date(fatura.periodo_fim).toLocaleDateString('pt-BR') : 'N/A'}
              </p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-700 mb-2">Itens da Fatura</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2">Descrição</th>
                    <th className="text-center p-2 w-16">Qtd</th>
                    <th className="text-right p-2 w-24">Valor Unit.</th>
                    <th className="text-right p-2 w-24">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {fatura.itens?.map((item, index) => {
                    const subtotal = (item.quantidade || 1) * (item.valor_unitario || 0);
                    return (
                      <tr key={index} className="border-t">
                        <td className="p-2">{item.descricao || 'Item'}</td>
                        <td className="p-2 text-center">{item.quantidade || 1}</td>
                        <td className="p-2 text-right">R$ {(item.valor_unitario || 0).toFixed(2)}</td>
                        <td className="p-2 text-right font-medium">R$ {subtotal.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr className="border-t-2">
                    <td colSpan={3} className="p-2 text-right font-semibold">TOTAL:</td>
                    <td className="p-2 text-right font-bold text-lg">
                      R$ {(fatura.valor_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          
          {fatura.status === 'pago' && fatura.data_pagamento && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-semibold text-emerald-800 mb-1 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Pagamento Confirmado
              </h4>
              <p className="text-emerald-700">
                Data do pagamento: {new Date(fatura.data_pagamento).toLocaleDateString('pt-BR')}
              </p>
            </div>
          )}
          
          {fatura.observacoes && (
            <div className="bg-amber-50 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-1">Observações</h4>
              <p className="text-amber-700">{fatura.observacoes}</p>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
          {fatura.status === 'pendente' && (
            <Button 
              className="bg-green-600 hover:bg-green-700" 
              onClick={() => { onMarcarPago(fatura.id); onOpenChange(false); }}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Marcar como Pago
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
